import { z } from 'zod';
import { CubeQuerySchema } from './cube-query-schema';

// ─── Value Format ───

export const ValueFormat = z.union([
  z.literal('currency'),
  z.literal('percent'),
  z.literal('number'),
  z.literal('integer'),
  z.literal('date'),
  z.literal('string'),
  z.literal('enum')
]);
export type ValueFormat = z.infer<typeof ValueFormat>;

// ─── Viz Variants ───

export const LineBarGraphViz = z.object({
  vizType: z
    .literal('line_bar_graph')
    .describe(
      'Time-series comparing planned/target line vs actual bar measures. Requires time dimension + planned measure + actual bar measures.'
    ),
  title: z
    .string()
    .min(1)
    .describe('Human-readable chart title describing what this visualization shows'),
  mapping: z.object({
    timeKey: z
      .string()
      .min(1)
      .describe('Exact Cube member ref for the time dimension (e.g. campaign_daily_metrics.date)'),
    plannedKey: z
      .string()
      .min(1)
      .describe(
        'Exact Cube member ref for the planned/target measure (e.g. campaign_daily_metrics.planned_spend)'
      ),
    barKeys: z
      .array(
        z
          .string()
          .min(1)
          .describe('Exact Cube member ref. May be a measure or a numeric dimension.')
      )
      .min(1)
      .describe('Array of Cube member refs for actual bar measures')
  })
});
export type LineBarGraphViz = z.infer<typeof LineBarGraphViz>;

export const BarChartViz = z.object({
  vizType: z
    .literal('bar_chart')
    .describe(
      'Compare values across categories. Horizontal when labels are long, stacked for composition.'
    ),
  title: z
    .string()
    .min(1)
    .describe('Human-readable chart title describing what this visualization shows'),
  orientation: z
    .union([z.literal('horizontal'), z.literal('vertical')])
    .describe('horizontal when category labels are long, vertical otherwise'),
  stacked: z.boolean().describe('true when showing composition across multiple measures'),
  mapping: z.object({
    categoryKey: z.string().min(1).describe('Exact Cube member ref for the categorical dimension'),
    valueKeys: z
      .array(
        z
          .string()
          .min(1)
          .describe('Exact Cube member ref. May be a measure or a numeric dimension.')
      )
      .min(1)
      .describe('Array of Cube member refs for numeric values')
  })
});
export type BarChartViz = z.infer<typeof BarChartViz>;

export const LineChartViz = z.object({
  vizType: z
    .literal('line_chart')
    .describe(
      'Trends over time without planned/actual split. Requires time dimension + numeric measures.'
    ),
  title: z
    .string()
    .min(1)
    .describe('Human-readable chart title describing what this visualization shows'),
  mapping: z.object({
    timeKey: z
      .string()
      .min(1)
      .describe('Exact Cube member ref for the time dimension (e.g. campaign_daily_metrics.date)'),
    lineKeys: z
      .array(
        z
          .string()
          .min(1)
          .describe('Exact Cube member ref. May be a measure or a numeric dimension.')
      )
      .min(1)
      .describe('Array of Cube member refs for line series')
  })
});
export type LineChartViz = z.infer<typeof LineChartViz>;

export const KpiCardViz = z.object({
  vizType: z
    .literal('kpi_card')
    .describe(
      'Single headline number with optional delta. Use when query returns exactly 1 row with 1-2 measures, no time dimension.'
    ),
  title: z
    .string()
    .min(1)
    .describe('Human-readable chart title describing what this visualization shows'),
  mapping: z.object({
    valueKey: z.string().min(1).describe('Exact Cube member ref for the primary value measure'),
    deltaKey: z
      .string()
      .min(1)
      .optional()
      .describe('Exact Cube member ref for the delta/change measure')
  }),
  deltaLabel: z
    .string()
    .optional()
    .describe("Label for the delta/change value, e.g. 'vs last month'")
});
export type KpiCardViz = z.infer<typeof KpiCardViz>;

export const PieDonutViz = z.object({
  vizType: z
    .literal('pie_donut')
    .describe(
      'Proportional distribution. Requires categorical dimension + single value. Avoid when > 8 categories (use bar_chart).'
    ),
  title: z
    .string()
    .min(1)
    .describe('Human-readable chart title describing what this visualization shows'),
  mapping: z.object({
    categoryKey: z.string().min(1).describe('Exact Cube member ref for the categorical dimension'),
    valueKey: z
      .string()
      .min(1)
      .describe('Exact Cube member ref. May be a measure or a numeric dimension.')
  })
});
export type PieDonutViz = z.infer<typeof PieDonutViz>;

export const TableColumnDef = z.object({
  key: z.string().min(1).describe('Exact Cube member ref')
});
export type TableColumnDef = z.infer<typeof TableColumnDef>;

export const TableViz = z.object({
  vizType: z
    .literal('table')
    .describe(
      'Detailed multi-column data. Use as fallback when no other chart type fits the data shape, or when user wants raw data.'
    ),
  title: z
    .string()
    .min(1)
    .describe('Human-readable chart title describing what this visualization shows'),
  columns: z.array(TableColumnDef).min(1)
});
export type TableViz = z.infer<typeof TableViz>;

export const NoDataViz = z.object({
  vizType: z.literal('no_data').describe('Use only when the query returned zero rows.'),
  reason: z.string().min(1).describe('Brief explanation of why no data was returned')
});
export type NoDataViz = z.infer<typeof NoDataViz>;

// ─── Discriminated Union ───

export const VizSpec = z.discriminatedUnion('vizType', [
  LineBarGraphViz,
  BarChartViz,
  LineChartViz,
  KpiCardViz,
  PieDonutViz,
  TableViz,
  NoDataViz
]);
export type VizSpec = z.infer<typeof VizSpec>;

// ─── Agent Result ───

export const AiVizResult = z.object({
  cubeQuery: CubeQuerySchema,
  viz: VizSpec
});
export type AiVizResult = z.infer<typeof AiVizResult>;
